import React from 'react';
import { Button, Grid, GridSize, CircularProgress, Typography, Box, FormControlLabel, Radio, RadioGroup, FormControl } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { SelectOption } from '../../../modules/shared/model';
import AutocompleteFilter from '../../../modules/shared/form/AutocompleteFilter';
import SelectInput from '../../../modules/shared/form/SelectInput';
import FormInput from '../../../modules/shared/form/FormInput';
import DateSelectFilter from '../../../modules/shared/form/DateSelectFilter';
import { RuntimeMetricsAutoTreatContext } from '../contexts/RuntimeMetricsAutoTreatContext';
import PaginationBanner from '../../../modules/shared/machineManagement/PaginationBanner';
import convertToLocalTime from '../../../modules/shared/utils/convertToLocalTime';
import { MachinesContext } from '../../../modules/machinesAdminManagement/contexts/MachinesContext';
import ConfirmCancelModal from '../../../modules/shared/modal/ConfirmCancelModal';
import { SnackBarContext } from '../../../modules/snackBar/contexts/SnackBarContext';

export interface FilterProps {
  label: string;
  pluralLabel: string;
  selector: string;
  width: boolean | GridSize;
  type: string;
  optionSelector: string;
  options?: SelectOption[];
}

interface RuntimeMetricsAutoTreatFilterBarProps {
  filters?: FilterProps[];
  filterValues?: any;
  machineChoices?: any;
  machineChoicesLoading?: boolean;
  filterChoices?: any;
  tableFilters?: any;
  setTableFilters?: any;
}

const RuntimeMetricsAutoTreatFilterBar: React.FC<RuntimeMetricsAutoTreatFilterBarProps> = (props) => {
  const {
    filters = [],
    filterValues,
    filterChoices,
    machineChoices,
    machineChoicesLoading,
    tableFilters,
    setTableFilters,
  } = props;

  const { user } = useAuth0();
  const { showErrorSnackBar, showSuccessSnackBar } = React.useContext(SnackBarContext);

  const [temporaryValues, setTemporaryValues] = React.useState<Record<string, any[]>>(filterValues);
  const [onPremPage, setOnPremPage] = React.useState<number>(0);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [includeHeader, setIncludeHeader] = React.useState<boolean>(true);

  const {
    runtimeMetricsAutoTreat,
    fetchRuntimeMetricsAutoTreat,
    fetchCachedRuntimeMetricsAutoTreat,
    runtimeMetricsAutoTreatLoading,
    runtimeMetricsAutoTreatRefreshedAt,
    activeMachine,
    setActiveMachine,
    fetchAlarmHistory,
    alarmHistory,
    exportRuntimeMetrics,
  } = React.useContext(RuntimeMetricsAutoTreatContext);
  const { autoTreatMachines } = React.useContext(MachinesContext);

  React.useEffect(() => {
    if (runtimeMetricsAutoTreat.length === 0 && localStorage.getItem('machineManagementAutoTreatMachine')) {
      fetchCachedRuntimeMetricsAutoTreat(localStorage.getItem('machineManagementAutoTreatMachine'));
      fetchRuntimeMetricsAutoTreat(localStorage.getItem('machineManagementAutoTreatMachine'), '');
    }
  }, []);

  React.useEffect(() => {
    if (localStorage.getItem('machineManagementAutoTreatMachine')) {
      setActiveMachine(
        autoTreatMachines.filter(
          (choice) => choice?.value === localStorage.getItem('machineManagementAutoTreatMachine'),
        )?.[0],
      );
    }
  }, [autoTreatMachines]);

  
  const handleMachineChange = (value: string) => {
    localStorage.setItem('machineManagementAutoTreatMachine', value);
    fetchCachedRuntimeMetricsAutoTreat(value);
    setActiveMachine(machineChoices.filter((choice) => choice?.value === value)?.[0]);
  };

  const defaultMachineChoice = () => {
    if (machineChoicesLoading) {
      return { label: 'Loading...', value: 'Select A Machine' };
    }

    return { label: 'Select A Machine', value: 'Select A Machine' };
  };

  const handleCancel = () => {
    window.location.reload();
  };

  const refreshDisabled = () => {
    return (
      runtimeMetricsAutoTreatLoading ||
      activeMachine?.value === 'Select A Machine' ||
      !activeMachine?.subscription_active ||
      activeMachine?.online_status === 'offline'
    );
  };

  const handleLoadData = () => {
    fetchRuntimeMetricsAutoTreat(activeMachine?.value, '');
  };

  const handleExport = () => {
    const localTime = new Date().toString();
    const utcOffset = new Date().getTimezoneOffset();

    exportRuntimeMetrics(includeHeader, localTime, utcOffset)
      .then(() => {
        showSuccessSnackBar('Runtime metrics exported successfully. Your download should begin shortly.');
        setModalOpen(false);
      })
      .catch((error) => {
        setModalOpen(false);
        showErrorSnackBar(`There was an issue: ${error?.response?.data?.message || error.message}`);
      });
  };

  const getTotalRecords = () => {
    const orderMetricsCount = runtimeMetricsAutoTreat?.length || 0;
    const alarmMetricsCount = alarmHistory?.length || 0;
    return `${orderMetricsCount} order metrics and ${alarmMetricsCount} alarm metrics`;
  };

  return (
    <Grid container spacing={1} alignItems="flex-start">
      <ConfirmCancelModal
        isOpen={modalOpen}
        onCancel={() => setModalOpen(false)}
        onConfirm={handleExport}
        title="Export Runtime Metrics"
        hasFooterBar
        confirmLabel="Export"
      >
        <div style={{ margin: '10px 20px' }}>
          <Typography style={{ marginLeft: -15 }} variant="h2" component="h2">
            Customization Options
          </Typography>
          <p style={{ marginTop: 7 }}>Include filter values in header?</p>
          <Box sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
            <FormControl>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="Include Header"
                value={includeHeader.toString()}
                onChange={(event) => setIncludeHeader(event.target.value === 'true')}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </Box>
        </div>
      </ConfirmCancelModal>
      <Grid item xs={12}>
        <Grid container spacing={1} alignItems="flex-start">
          <Grid item style={{ position: 'relative', top: '-3px' }}>
            <DateSelectFilter onTimePeriodChange={() => {}} />
          </Grid>
          <Grid item md={2} xs={6} style={{ position: 'relative', top: '1px' }}>
            <SelectInput
              value={activeMachine?.value}
              groupedChoices={{
                'Online Machines': machineChoices.filter(
                  (machine) => machine.subscription_active && machine.online_status === 'online',
                ),
                'Offline Machines': machineChoices.filter(
                  (machine) => machine.subscription_active && machine.online_status === 'offline',
                ),
                'Expired Machines': machineChoices.filter((machine) => machine.subscription_active === false),
              }}
              defaultChoice={defaultMachineChoice()}
              onChange={handleMachineChange}
              label="Machine"
              customPadding="8px 27px 9px 15px"
            />
          </Grid>
          {filters.map((filter) => (
            <React.Fragment key={filter.label}>
              <Grid item>
                <AutocompleteFilter
                  label={filter.label}
                  loading={false}
                  pluralLabel={filter.pluralLabel}
                  choices={filter?.options || filterChoices[filter?.optionSelector] || []}
                  setValue={(value) => {
                    setTemporaryValues({ ...temporaryValues, [filter?.selector]: value });
                  }}
                  value={temporaryValues[filter?.selector] as string[]}
                />
              </Grid>
            </React.Fragment>
          ))}
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              style={{ color: '#fff', marginTop: 19 }}
              disableElevation
              disabled={refreshDisabled()}
              onClick={() => {
                fetchRuntimeMetricsAutoTreat(activeMachine?.value, '');
                fetchAlarmHistory(activeMachine?.value);
              }}
            >
              {runtimeMetricsAutoTreatRefreshedAt ? 'Load' : 'Load'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              style={{ marginTop: 19, marginLeft: 13 }}
              disableElevation
              disabled={refreshDisabled() || runtimeMetricsAutoTreatLoading}
              onClick={() => setModalOpen(true)}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              style={{ color: '#fff', marginTop: 19, marginLeft: 13 }}
              disableElevation
              disabled={!runtimeMetricsAutoTreatLoading}
              onClick={() => {
                handleCancel();
              }}
            >
              Cancel
            </Button>
            {runtimeMetricsAutoTreatLoading ? (
              <span style={{ marginLeft: 20, position: 'relative', top: 19 }}>
                <CircularProgress style={{ width: 30, height: 30 }} />
              </span>
            ) : (
              <span style={{ marginLeft: 7, position: 'relative', top: 10 }}>
                Last Loaded: {convertToLocalTime(runtimeMetricsAutoTreatRefreshedAt)}
              </span>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {runtimeMetricsAutoTreat?.length === 500 && (
            <PaginationBanner
              totalRecords={runtimeMetricsAutoTreat?.length}
              currentPage={onPremPage}
              onPageChange={(newPage) => setOnPremPage(newPage)}
            />
          )}
        </Grid>
      </Grid>

      {/* Live Filters - Hidden but logic preserved */}
      {/* <br />
      <Typography variant="h2" component="h2" style={{ marginTop: 10, marginBottom: -18, marginLeft: 5 }}>
        Live Filters
      </Typography>
      <Grid item xs={12} style={{ marginTop: 20 }}>
        <Grid container spacing={1} alignItems="flex-start">
          <Grid item md={2} xs={6}>
            <FormInput
              value={tableFilters?.orderName}
              onChange={(value) => setTableFilters({ ...tableFilters, orderName: value })}
              label="Order Name"
            />
          </Grid>
          <Grid item md={2} xs={6}>
            <FormInput
              value={tableFilters?.orderOperator}
              onChange={(value) => setTableFilters({ ...tableFilters, orderOperator: value })}
              label="Operator"
            />
          </Grid>
          <Grid item md={2} xs={6}>
            <FormInput
              value={tableFilters?.orderVariety}
              onChange={(value) => setTableFilters({ ...tableFilters, orderVariety: value })}
              label="Variety"
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              style={{ color: '#fff', marginTop: 19 }}
              disableElevation
              onClick={() =>
                setTableFilters({
                  ...tableFilters,
                  orderName: '',
                  orderPartnerID: '',
                  orderVariety: '',
                  orderRecipe: '',
                })
              }
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Grid> */}
    </Grid>
  );
};

export default RuntimeMetricsAutoTreatFilterBar;
